import React, { useEffect, useState } from 'react';

import Confetti from 'react-confetti';

import useWindowSize from '@components/common/hooks/useWindowSize';
import GreetingCard from '@components/lpc/GreetingCard/GreetingCard';
import Layout from '@components/lpc/Layout/Layout';
import { potType } from '@propTypes/potTypes';
import greetingCardService from '@services/domain/Lpc/GreetingCardService';
import PotService from '@services/domain/Lpc/PotService';
import { isInternalServerError, isNotFoundError } from '@services/http/Http';

import css from './potGreetingCardPage.module.scss';
import Custom404 from '../../../404';
import Custom500 from '../../../500';

type PotGreetingCardProps = {
    pot: potType;
    potRef: string;
    internalError: boolean;
    potNotFound: boolean;
    initialPot: potType;
    greetingCardInformations: {
        disableGreetingCardPO: boolean;
        hasDarkThemedGreetingCard: boolean;
        hasAnimatedGreetingCardPO: boolean;
    };
};

const DARK_THEME = {
    textPrimaryVariant: 'white',
    textPrimaryColor: '#FFFFFF',
    backgroundColorPrimary: '#0b0c0f',
    backgroundColorSecondary: '#191a1f',
    backgroundImage: '/front-static/images/greeting-card/dark-theme-bg.png',
};

const LIGHT_THEME = {
    textPrimaryVariant: 'txt-primary',
    textPrimaryColor: '#232e69',
    backgroundColorPrimary: '#f2f7fc',
    backgroundColorSecondary: '#FFFFFF',
    backgroundImage: '/front-static/images/greeting-card/light-theme-bg.png',
};

const PotGreetingCard = ({ pot, internalError, greetingCardInformations, potNotFound }: PotGreetingCardProps) => {
    const { width, height } = useWindowSize();
    const { disableGreetingCardPO, hasDarkThemedGreetingCard, hasAnimatedGreetingCardPO } = greetingCardInformations;
    const [recycle, setRecycle] = useState(true);

    const [theme, setTheme] = useState({
        textPrimaryVariant: '',
        textPrimaryColor: '',
        backgroundColorPrimary: '',
        backgroundColorSecondary: '',
        backgroundImage: '',
    });

    useEffect(() => {
        const timer = setTimeout(() => setRecycle(false), 4000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        hasDarkThemedGreetingCard ? setTheme(DARK_THEME) : setTheme(LIGHT_THEME);
    }, [hasDarkThemedGreetingCard]);

    if (internalError) {
        return <Custom500 />;
    }

    if (potNotFound) {
        return <Custom404 potNotFound />;
    }

    if (disableGreetingCardPO) {
        return <Custom404 />;
    }

    return (
        <Layout
            isGreetingCardPage
            potCategory={pot.category}
            hideFooter
            potTitle={pot.title}
            potRef={pot.uniq}
            hideDropDown
        >
            <div className={css.wrapper}>
                <div
                    className={css.potGreetingCardPageWrapper}
                    style={{
                        backgroundColor: theme.backgroundColorPrimary,
                        backgroundImage: `url(${theme.backgroundImage})`,
                    }}
                >
                    {width && height && hasAnimatedGreetingCardPO && pot.category !== 'BEREAVEMENT' && (
                        <Confetti
                            colors={['#E22B76', '#00A5E2', '#FAC700']}
                            width={width}
                            height={height}
                            recycle={recycle}
                        />
                    )}
                    <GreetingCard
                        category={pot.category}
                        personRole={pot.personRole}
                        potRef={pot.uniq}
                        title={pot.title}
                        theme={theme}
                    />
                </div>
            </div>
        </Layout>
    );
};
export default PotGreetingCard;

export const getServerSideProps = async (ctx) => {
    const reference = await ctx.query.uniqPO;
    let internalError = false;
    let notFoundError = false;

    const pot = await PotService.getPot(reference, ctx).catch((err) => {
        notFoundError = isNotFoundError(err);
        internalError = internalError || isInternalServerError(err);
    });

    const greetingCardInformations = await greetingCardService
        .getGreetingCardInformations(pot.uniq, ctx)
        .catch((err) => {
            internalError = internalError || isInternalServerError(err);
        });

    return {
        props: {
            pot: pot || {},
            greetingCardInformations: greetingCardInformations || {},
            internalError: internalError,
            potNotFound: notFoundError,
        },
    };
};
